import { cn } from '@/src/core/utils/cn'
import { forwardRef, useCallback, useMemo, useState } from 'react'

export interface SelectOption {
	value: string
	label: string
	disabled?: boolean
	metadata?: Record<string, unknown>
}

export interface SelectProperties {
	options: SelectOption[]
	value?: string
	defaultValue?: string
	placeholder?: string
	disabled?: boolean
	error?: boolean
	className?: string
	onChange?: (value: string, option: SelectOption) => void
	renderOption?: (option: SelectOption) => React.ReactNode
	'aria-label'?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProperties>(
	(
		{
			options,
			value,
			defaultValue,
			placeholder = 'Select an option',
			disabled = false,
			error = false,
			className,
			onChange,
			renderOption,
			'aria-label': ariaLabel,
			...properties
		},
		reference
	) => {
		const [isOpen, setIsOpen] = useState(false)
		const [selectedValue, setSelectedValue] = useState(value || defaultValue || '')

		const selectedOption = options.find((option) => option.value === selectedValue)

		const handleOptionClick = useCallback(
			(option: SelectOption) => {
				if (option.disabled) return

				setSelectedValue(option.value)
				setIsOpen(false)
				onChange?.(option.value, option)
			},
			[onChange]
		)

		const optionClickHandlers = useMemo(
			() =>
				new Map<string, () => void>(options.map((option) => [option.value, () => handleOptionClick(option)])),
			[options, handleOptionClick]
		)

		const handleNativeSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
			const option = options.find((option_) => option_.value === event.target.value)
			if (option) {
				setSelectedValue(option.value)
				onChange?.(option.value, option)
			}
		}

		const handleToggleOpen = useCallback(() => {
			if (!disabled) {
				setIsOpen(!isOpen)
			}
		}, [disabled, isOpen])

		// Fallback to native select for better accessibility if renderOption is not provided
		if (!renderOption) {
			return (
				<select
					ref={reference}
					value={selectedValue}
					disabled={disabled}
					aria-label={ariaLabel}
					className={cn(
						'block w-full rounded-md border border-gray-500 bg-transparent px-3 py-2 text-white',
						'focus:ring-2 focus:ring-[#66d697] focus:outline-none',
						error && 'border-red-500 focus:ring-red-500',
						disabled && 'opacity-50 cursor-not-allowed',
						className
					)}
					onChange={handleNativeSelectChange}
					{...properties}
				>
					{placeholder ? <option value="">{placeholder}</option> : undefined}
					{options.map((option) => (
						<option key={option.value} value={option.value} disabled={option.disabled}>
							{option.label}
						</option>
					))}
				</select>
			)
		}

		return (
			<div className="relative">
				<button
					type="button"
					aria-haspopup="listbox"
					aria-expanded={isOpen}
					aria-label={ariaLabel}
					disabled={disabled}
					className={cn(
						'relative w-full cursor-pointer rounded-md border border-gray-500 bg-transparent px-3 py-2 text-left text-white',
						'focus:ring-2 focus:ring-[#66d697] focus:outline-none',
						error && 'border-red-500 focus:ring-red-500',
						disabled && 'opacity-50 cursor-not-allowed',
						className
					)}
					onClick={handleToggleOpen}
				>
					<span className="block truncate">{selectedOption ? selectedOption.label : placeholder}</span>
					<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
						<svg
							className={cn('h-5 w-5 text-white/50 transition-transform', isOpen && 'rotate-180')}
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fillRule="evenodd"
								d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
								clipRule="evenodd"
							/>
						</svg>
					</span>
				</button>

				{isOpen ? (
					<div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-500 bg-gray-900 py-1 shadow-lg">
						{options.map((option) => (
							<div
								key={option.value}
								className={cn(
									'relative cursor-pointer select-none px-3 py-2 text-white',
									option.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/10',
									selectedValue === option.value && 'bg-[#66d697]/20'
								)}
								onClick={optionClickHandlers.get(option.value)}
							>
								{renderOption(option)}
							</div>
						))}
					</div>
				) : undefined}
			</div>
		)
	}
)

Select.displayName = 'Select'
